<template>

  <div class="position-relative">
    <b-card class="mt-4">
      <!-- Add Category Button -->
      <b-button variant="info" @click="showCategoryModal(null)">
        <i class="fas fa-plus-square text-white" style="font-size: 13px;"></i> Add Fee Category
      </b-button>

      <!-- Fee Category Modal -->
      <b-modal id="CategoryModal" hide-footer centered>
        <div class="p-3 border rounded">
          <b-form-group>
            <label>Category Name</label><span class="text-danger">*</span>
            <b-form-input v-model.trim="categoryForm.name" placeholder="e.g. Merit, Management" />
            <span class="input-error-message" v-if="!$v.categoryForm.name.required && $v.categoryForm.name.$dirty">
              Category name is required!
            </span>
          </b-form-group>
          <b-form-group label="Description">
            <b-form-textarea v-model.trim="categoryForm.description" placeholder="Optional description" rows="2" />
          </b-form-group>
          <b-button variant="info" @click="saveCategory">
            {{ isEditingCategory ? 'Update Category' : 'Save Category' }}
          </b-button>
        </div>
      </b-modal>

      <!-- Fee Component Modal -->
      <b-modal id="ComponentModal" hide-footer centered>
        <div class="p-3 border rounded">
          <h6 v-if="activeCategory">
            {{ isEditingComponent ? 'Edit' : 'Add' }} Fee Component for:
            <strong>{{ activeCategory.name }}</strong>
          </h6>
          <b-form-group>
            <label>Component Name</label><span class="text-danger">*</span>
            <b-form-input v-model.trim="componentForm.name" placeholder="e.g. Tuition Fee" />
            <span class="input-error-message" v-if="!$v.componentForm.name.required && $v.componentForm.name.$dirty">
              Component name is required!
            </span>
          </b-form-group>
          <b-form-group>
            <label>Amount (₹)</label><span class="text-danger">*</span>
            <b-form-input v-model.number="componentForm.amount" type="number" />
            <span class="input-error-message"
              v-if="!$v.componentForm.amount.required && $v.componentForm.amount.$dirty">
              Amount is required!
            </span>
          </b-form-group>
          <b-form-group label="Description">
            <b-form-textarea v-model.trim="componentForm.description" placeholder="Optional description" rows="2" />
          </b-form-group>
          <b-button variant="info" @click="saveComponent">
            {{ isEditingComponent ? 'Update Component' : 'Save Component' }}
          </b-button>
        </div>
      </b-modal>

      <!-- Table Section -->
      <div class="table-responsive rounded mt-3">
        <table class="table table-striped table-bordered">
          <thead class="thead-dark text-left">
            <tr>
              <th>#</th>
              <th>Fee Category</th>
              <th>Fee Components</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(category, index) in feeCategories" :key="category.id">
              <td>
                <div class="d-flex align-items-center">
                  <span>{{ index + 1 }}</span>

                  <div v-if="category.studentcategorymappings.length != 0"
                    class="ms-2 tooltip-wrapper d-inline-flex align-items-center">
                    <b-icon icon="lock-fill" variant="danger"></b-icon>
                    <div class="custom-tooltip">
                      <span>Mapped</span>
                    </div>
                  </div>
                </div>
              </td>

              <td>
                <div class="d-flex align-items-center justify-content-between">
                  <div>
                    <span>{{ category.name }}</span>
                    <small v-if="category.description" class="text-muted d-block">
                      {{ category.description }}
                    </small>
                  </div>
                  <span v-if="category.studentcategorymappings.length == 0">
                    <i class="fas fa-plus-circle text-success mx-1" style="cursor: pointer;font-size: 15px !important;"
                      @click="showComponentModal(category, null)"></i>
                    <i class="fas fa-edit text-info mx-1" style="cursor: pointer;font-size: 15px !important;"
                      @click="showCategoryModal(category)"></i>
                    <i class="fas fa-trash text-danger mx-1" style="cursor: pointer;font-size: 15px !important;"
                      @click="removeCategory(category.id)"></i>
                  </span>
                </div>
              </td>
              <td>
                <div v-if="category.components.length">
                  <ul class="list-unstyled mb-0">
                    <li v-for="(component, cIndex) in category.components" :key="component.id"
                      class="d-flex justify-content-between align-items-center py-2"
                      :class="{ 'border-bottom': cIndex !== category.components.length - 1 }">
                      <div>
                        <span>
                          {{ component.name }} – ₹{{ parseFloat(component.amount).toFixed(2) }}
                        </span>
                        <small v-if="component.description" class="text-muted d-block">
                          {{ component.description }}
                        </small>
                      </div>
                      <span v-if="category.studentcategorymappings.length == 0">
                        <i class="fas fa-edit text-info mx-1" style="cursor: pointer;font-size: 15px !important;"
                          @click="showComponentModal(category, component)"></i>
                        <i class="fas fa-trash text-danger mx-1" style="cursor: pointer;font-size: 15px !important;"
                          @click="removeComponent(category.id, component.id)"></i>
                      </span>
                    </li>
                  </ul>
                </div>
                <div v-else class="text-muted font-italic">No components available.</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-card>
  </div>
</template>

<script>
import { required } from "vuelidate/lib/validators";

export default {
  data() {
    return {
      feeCategories: [],
      activeCategory: null,
      isEditingCategory: false,
      isEditingComponent: false,
      categoryForm: {
        id: null,
        name: '',
        description: ''
      },
      componentForm: {
        id: null,
        name: '',
        amount: '',
        description: '',
        fee_category_id: null
      },
    };
  },
  validations: {
    categoryForm: {
      name: { required }
    },
    componentForm: {
      name: { required },
      amount: { required }
    }
  },
  async created() {
    await this.loadFeeCategories();
  },
  methods: {
    async loadFeeCategories() {
      const url = this.$store.getters.getAPIKey.mainAPI + `/feecategories`;
      const response = await this.$axios.get(url);
      this.feeCategories = response.data;
    },
    showCategoryModal(category) {
      this.isEditingCategory = !!category;
      this.categoryForm = category
        ? { id: category.id, name: category.name, description: category.description || '' }
        : { id: null, name: '', description: '' };
      this.$v.categoryForm.$reset();
      this.$bvModal.show('CategoryModal');
    },
    async saveCategory() {
      this.$v.categoryForm.$touch();
      if (this.$v.categoryForm.$invalid) return;

      const { id, name, description } = this.categoryForm;
      const url = this.$store.getters.getAPIKey.mainAPI + `/feecategories`;

      if (this.isEditingCategory) {
        await this.$axios.put(`${url}/${id}`, { name, description });
        this.$toast.success("Category updated successfully");
      } else {
        await this.$axios.post(url, { name, description });
        this.$toast.success("Category added successfully");
      }

      this.$bvModal.hide('CategoryModal');
      this.loadFeeCategories();
    },
    async removeCategory(id) {
      if (!confirm("Delete this category?")) return;
      const url = this.$store.getters.getAPIKey.mainAPI + `/feecategories/${id}`;
      await this.$axios.delete(url);
      this.$toast.success("Category deleted");
      this.loadFeeCategories();
    },
    showComponentModal(category, component) {
      this.activeCategory = category;
      this.isEditingComponent = !!component;
      this.componentForm = component
        ? {
          id: component.id,
          name: component.name,
          amount: component.amount,
          description: component.description || '',
          fee_category_id: category.id
        }
        : {
          id: null,
          name: '',
          amount: '',
          description: '',
          fee_category_id: category.id
        };
      this.$v.componentForm.$reset();
      this.$bvModal.show('ComponentModal');
    },
    async saveComponent() {
      this.$v.componentForm.$touch();
      if (this.$v.componentForm.$invalid) return;

      const { id, name, amount, description, fee_category_id } = this.componentForm;
      const url = this.$store.getters.getAPIKey.mainAPI;

      if (this.isEditingComponent) {
        await this.$axios.put(`${url}/feecomponents/${id}`, {
          name, amount, description, fee_category_id
        });
        this.$toast.success("Component updated");
      } else {
        await this.$axios.post(`${url}/feecategories/${fee_category_id}/feecomponents`, {
          name, amount, description
        });
        this.$toast.success("Component added");
      }

      this.$bvModal.hide('ComponentModal');
      this.loadFeeCategories();
    },
    async removeComponent(categoryId, componentId) {
      if (!confirm("Delete this component?")) return;
      const url = this.$store.getters.getAPIKey.mainAPI +
        `/feecategories/${categoryId}/feecomponents/${componentId}`;
      await this.$axios.delete(url);
      this.$toast.success("Component deleted");
      this.loadFeeCategories();
    }
  }
};
</script>

<style scoped>
.form-group-error {
  border: 1px solid #dc3545 !important;
}

.input-error-message {
  color: #dc3545;
  font-size: 12px;
  margin-top: -10px;
  margin-bottom: 10px;
  display: block;
}

.tooltip-wrapper {
  position: relative;
  display: inline-block;
  cursor: pointer;
}

.custom-tooltip {
  visibility: hidden;
  background-color: #000;
  color: #fff;
  padding: 6px 10px;
  border-radius: 6px;
  position: absolute;
  z-index: 999;
  top: 100%;
  /* position below */
  left: 0;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.3s;
  margin-top: 5px;
  font-size: 13px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.2);
}

.tooltip-wrapper:hover .custom-tooltip {
  visibility: visible;
  opacity: 1;
}
</style>
